import React, { useState, useEffect, useMemo } from 'react';
import {
  GraduationCap,
  Users,
  BookOpen,
  UserCheck,
  Edit3,
  Save,
  X,
  Search,
  Download,
  Calendar,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react';
import { toast } from 'react-toastify';
import { schoolDataApi } from '../services/api';
import { SchoolClass, Teacher, Subject, Student, AttendanceEntry } from '../types';
import { sortClasses } from '../utils/classUtils';
import { formatDate, getAge, exportStudentsToExcel } from '../utils/helpers';
import { Modal } from '../components/ui';

type TabKey = 'details' | 'subjects' | 'students' | 'attendance';

const todayIso = () => new Date().toISOString().slice(0, 10);
const subjectKey = (s: Pick<Subject, 'name' | 'code'>) => `${s.name.trim().toLowerCase()}|${s.code.trim().toLowerCase()}`;

const ClassManagement: React.FC = () => {
  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [students, setStudents] = useState<Student[]>([]);

  const [selectedClass, setSelectedClass] = useState<string>('');
  const [selectedSection, setSelectedSection] = useState<string>('');
  const [activeTab, setActiveTab] = useState<TabKey>('details');
  const [loading, setLoading] = useState(true);
  const [loadingClassDetails, setLoadingClassDetails] = useState(false);

  // Class teacher management
  const [classTeacher, setClassTeacher] = useState<string>('');
  const [isEditingTeacher, setIsEditingTeacher] = useState(false);
  const [savingTeacher, setSavingTeacher] = useState(false);

  // Class editing (basic info only — subjects have their own dialog)
  const [isEditingClass, setIsEditingClass] = useState(false);
  const [editClassData, setEditClassData] = useState({
    capacity: 0,
    description: ''
  });
  const [savingClass, setSavingClass] = useState(false);

  // Subject assignment
  const [assignedSubjects, setAssignedSubjects] = useState<string[]>([]);
  const [isEditingSubjects, setIsEditingSubjects] = useState(false);
  const [editSubjectIds, setEditSubjectIds] = useState<string[]>([]);
  const [savingSubjects, setSavingSubjects] = useState(false);
  const [newSubjectName, setNewSubjectName] = useState('');
  const [newSubjectCode, setNewSubjectCode] = useState('');
  const [addingSubject, setAddingSubject] = useState(false);

  // Student list (Students tab)
  const [classStudents, setClassStudents] = useState<Student[]>([]);
  const [studentSearch, setStudentSearch] = useState('');

  // Attendance
  const [attendanceDate, setAttendanceDate] = useState(todayIso());
  const [attendanceEntries, setAttendanceEntries] = useState<Record<string, AttendanceEntry['status']>>({});
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [savingAttendance, setSavingAttendance] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedClass && selectedSection) {
      setActiveTab('details');
      loadClassData();
    }
  }, [selectedClass, selectedSection]);

  useEffect(() => {
    if (selectedClass && selectedSection && activeTab === 'attendance') {
      loadAttendance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, attendanceDate, classStudents]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [classesData, teachersData, subjectsData, studentsData] = await Promise.all([
        schoolDataApi.classes.getAll(),
        schoolDataApi.teachers.getAll(),
        schoolDataApi.subjects.getAll(),
        schoolDataApi.students.getAll()
      ]);

      setClasses(sortClasses(classesData.filter(cls => cls.isActive)));
      setTeachers(teachersData.filter(teacher => teacher.status === 'Active'));
      setSubjects(subjectsData.filter(subject => subject.isActive));
      setStudents(studentsData);
    } catch (error) {
      toast.error('Failed to load class management data');
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadClassData = async () => {
    if (!selectedClass || !selectedSection) return;

    try {
      setLoadingClassDetails(true);

      const currentClass = classes.find(cls => cls.name === selectedClass);
      if (!currentClass) {
        setLoadingClassDetails(false);
        return;
      }

      const classDetails = await schoolDataApi.classes.getById(currentClass.id);
      if (classDetails) {
        setClassTeacher(classDetails.classTeacher || '');
        setAssignedSubjects(Array.isArray(classDetails.subjects) ? classDetails.subjects : []);
      } else {
        setClassTeacher('');
        setAssignedSubjects([]);
      }

      const currentClassStudents = students.filter(
        student => student.class === selectedClass && student.section === selectedSection
      );
      setClassStudents(currentClassStudents);
    } catch (error) {
      console.error('Error loading class data:', error);
      toast.error('Failed to load class details');
    } finally {
      setLoadingClassDetails(false);
    }
  };

  const loadAttendance = async () => {
    const currentClass = classes.find(cls => cls.name === selectedClass);
    if (!currentClass) return;

    try {
      setLoadingAttendance(true);
      const record = await schoolDataApi.attendance.getByClassDate(currentClass.id, selectedSection, attendanceDate);
      const entries: Record<string, AttendanceEntry['status']> = {};
      classStudents.forEach(student => {
        const existing = record?.entries.find(e => e.studentId === student.id);
        entries[student.id] = existing?.status || 'Present';
      });
      setAttendanceEntries(entries);
    } catch (error) {
      console.error('Error loading attendance:', error);
      toast.error('Failed to load attendance');
    } finally {
      setLoadingAttendance(false);
    }
  };

  const handleClassChange = (className: string) => {
    setSelectedClass(className);
    setSelectedSection('');
  };

  const getSelectedClassData = () => {
    return classes.find(cls => cls.name === selectedClass);
  };

  const handleAssignTeacher = async () => {
    const currentClass = getSelectedClassData();
    if (!currentClass) return;

    try {
      setSavingTeacher(true);
      await schoolDataApi.classes.update(currentClass.id, {
        name: currentClass.name,
        sections: currentClass.sections,
        isActive: currentClass.isActive,
        ...(currentClass.capacity !== undefined ? { capacity: currentClass.capacity } : {}),
        ...(currentClass.description !== undefined ? { description: currentClass.description } : {}),
        classTeacher
      });
      setIsEditingTeacher(false);
      toast.success('Class teacher updated');
    } catch (error) {
      toast.error('Failed to assign class teacher');
      console.error('Error assigning teacher:', error);
    } finally {
      setSavingTeacher(false);
    }
  };

  const handleEditClass = () => {
    const currentClass = getSelectedClassData();
    if (currentClass) {
      setEditClassData({
        capacity: currentClass.capacity || 0,
        description: currentClass.description || ''
      });
    }
    setIsEditingClass(true);
  };

  const handleUpdateClass = async () => {
    const currentClass = getSelectedClassData();
    if (!currentClass) return;

    try {
      setSavingClass(true);
      await schoolDataApi.classes.update(currentClass.id, {
        name: currentClass.name,
        sections: currentClass.sections,
        isActive: currentClass.isActive,
        capacity: editClassData.capacity,
        description: editClassData.description
      });

      setClasses(prev =>
        prev.map(cls =>
          cls.id === currentClass.id
            ? { ...cls, capacity: editClassData.capacity, description: editClassData.description, updatedAt: new Date().toISOString() }
            : cls
        )
      );

      setIsEditingClass(false);
      toast.success('Class details updated');
    } catch (error) {
      toast.error('Failed to update class');
      console.error('Error updating class:', error);
    } finally {
      setSavingClass(false);
    }
  };

  const handleOpenEditSubjects = () => {
    setEditSubjectIds([...assignedSubjects]);
    setNewSubjectName('');
    setNewSubjectCode('');
    setIsEditingSubjects(true);
  };

  const handleToggleSubject = (subject: Subject) => {
    const groupIds = subjectIdsByKey.get(subjectKey(subject)) || [subject.id];
    setEditSubjectIds(prev => {
      const isSelected = prev.some(id => groupIds.includes(id));
      const withoutGroup = prev.filter(id => !groupIds.includes(id));
      // Selecting always converges on the canonical (first-seen) id for this
      // subject, so any older duplicate ids get quietly cleaned up on save.
      return isSelected ? withoutGroup : [...withoutGroup, subject.id];
    });
  };

  const handleAddSubject = async () => {
    const name = newSubjectName.trim();
    const code = newSubjectCode.trim();
    if (!name || !code) return;

    const key = subjectKey({ name, code });
    const existing = uniqueSubjects.find(s => subjectKey(s) === key);
    if (existing) {
      setEditSubjectIds(prev => (prev.includes(existing.id) ? prev : [...prev, existing.id]));
      setNewSubjectName('');
      setNewSubjectCode('');
      toast.info(`"${existing.name}" already exists — selected it for this class`);
      return;
    }

    try {
      setAddingSubject(true);
      const created = await schoolDataApi.subjects.create({
        name,
        code,
        description: '',
        isActive: true
      });
      setSubjects(prev => [...prev, created]);
      setEditSubjectIds(prev => [...prev, created.id]);
      setNewSubjectName('');
      setNewSubjectCode('');
      toast.success(`"${created.name}" added`);
    } catch (error) {
      toast.error('Failed to add subject');
      console.error('Error adding subject:', error);
    } finally {
      setAddingSubject(false);
    }
  };

  const handleSaveSubjects = async () => {
    const currentClass = getSelectedClassData();
    if (!currentClass) return;

    try {
      setSavingSubjects(true);
      await schoolDataApi.classes.update(currentClass.id, {
        name: currentClass.name,
        sections: currentClass.sections,
        isActive: currentClass.isActive,
        ...(currentClass.capacity !== undefined ? { capacity: currentClass.capacity } : {}),
        ...(currentClass.description !== undefined ? { description: currentClass.description } : {}),
        subjects: editSubjectIds
      });

      setAssignedSubjects(editSubjectIds);
      setIsEditingSubjects(false);
      toast.success('Subjects updated');
    } catch (error) {
      toast.error('Failed to update subjects');
      console.error('Error updating subjects:', error);
    } finally {
      setSavingSubjects(false);
    }
  };

  const handleAttendanceStatusChange = (studentId: string, status: AttendanceEntry['status']) => {
    setAttendanceEntries(prev => ({ ...prev, [studentId]: status }));
  };

  const markAll = (status: AttendanceEntry['status']) => {
    const entries: Record<string, AttendanceEntry['status']> = {};
    classStudents.forEach(student => { entries[student.id] = status; });
    setAttendanceEntries(entries);
  };

  const handleSaveAttendance = async () => {
    const currentClass = getSelectedClassData();
    if (!currentClass) return;

    try {
      setSavingAttendance(true);
      const entries: AttendanceEntry[] = classStudents.map(student => ({
        studentId: student.id,
        status: attendanceEntries[student.id] || 'Present'
      }));

      await schoolDataApi.attendance.save({
        classId: currentClass.id,
        className: currentClass.name,
        section: selectedSection,
        date: attendanceDate,
        entries
      });

      toast.success(`Attendance saved for ${formatDate(attendanceDate)}`);
    } catch (error) {
      toast.error('Failed to save attendance');
      console.error('Error saving attendance:', error);
    } finally {
      setSavingAttendance(false);
    }
  };

  const filteredClassStudents = useMemo(() => {
    if (!studentSearch.trim()) return classStudents;
    const term = studentSearch.toLowerCase();
    return classStudents.filter(s =>
      s.name.toLowerCase().includes(term) || s.studentId.toLowerCase().includes(term)
    );
  }, [classStudents, studentSearch]);

  const attendanceSummary = useMemo(() => {
    const values = Object.values(attendanceEntries);
    return {
      present: values.filter(v => v === 'Present').length,
      absent: values.filter(v => v === 'Absent').length,
      leave: values.filter(v => v === 'Leave').length
    };
  }, [attendanceEntries]);

  // Subjects are sometimes seeded/created with the same name+code more than once
  // (e.g. "Mathematics" existing once per class-level bracket). Group by that key
  // so the picker and read-only view each show one entry per real-world subject.
  const subjectIdsByKey = useMemo(() => {
    const map = new Map<string, string[]>();
    subjects.forEach(s => {
      const key = subjectKey(s);
      map.set(key, [...(map.get(key) || []), s.id]);
    });
    return map;
  }, [subjects]);

  const uniqueSubjects = useMemo(() => {
    const seen = new Map<string, Subject>();
    subjects.forEach(s => {
      const key = subjectKey(s);
      if (!seen.has(key)) seen.set(key, s);
    });
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [subjects]);

  const assignedUniqueSubjects = useMemo(() => {
    const seen = new Set<string>();
    const result: Subject[] = [];
    assignedSubjects.forEach(id => {
      const subject = subjects.find(s => s.id === id);
      if (!subject) return;
      const key = subjectKey(subject);
      if (seen.has(key)) return;
      seen.add(key);
      result.push(subject);
    });
    return result;
  }, [assignedSubjects, subjects]);

  const capacity = getSelectedClassData()?.capacity;
  const capacityPercent = capacity ? Math.min(100, Math.round((classStudents.length / capacity) * 100)) : null;
  const capacityColor =
    capacityPercent === null ? '#8a7f6e' : capacityPercent >= 100 ? '#f38b83' : capacityPercent >= 80 ? '#f0b64c' : '#4ade80';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#e2916d] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'details', label: 'Class Details' },
    { key: 'subjects', label: 'Subjects' },
    { key: 'students', label: 'Students' },
    { key: 'attendance', label: 'Attendance' }
  ];

  return (
    <div className="space-y-6">
      {/* Header with Class & Section selection in the same row */}
      <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-6">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#f3ede2]">Class Management</h1>
            <p className="text-[#b9ad9b] mt-1">Manage class assignments, teachers, subjects, and students</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 lg:min-w-[420px]">
            <div className="flex-1">
              <label className="block text-xs font-medium text-[#8a7f6e] mb-1">Class</label>
              <select
                value={selectedClass}
                onChange={(e) => handleClassChange(e.target.value)}
                className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
              >
                <option value="">Select Class</option>
                {classes.map((cls) => (
                  <option key={cls.id} value={cls.name}>{cls.name}</option>
                ))}
              </select>
            </div>

            <div className="flex-1">
              <label className="block text-xs font-medium text-[#8a7f6e] mb-1">Section</label>
              <select
                value={selectedSection}
                onChange={(e) => setSelectedSection(e.target.value)}
                className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2] disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!selectedClass}
              >
                <option value="">Select Section</option>
                {selectedClass && getSelectedClassData()?.sections.map((section) => (
                  <option key={section} value={section}>{section}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {!selectedClass || !selectedSection ? (
        <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-12 text-center">
          <GraduationCap className="w-16 h-16 text-[#8a7f6e] mx-auto mb-4" />
          <h3 className="text-lg font-medium text-[#f3ede2] mb-2">Select a Class to Get Started</h3>
          <p className="text-[#8a7f6e]">
            Choose a class and section above to manage its teacher, subjects, students, and attendance.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Quick stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-4">
              <p className="text-xs text-[#8a7f6e]">Students</p>
              <p className="text-xl font-bold text-[#f3ede2] mt-1">
                {classStudents.length}{capacity ? ` / ${capacity}` : ''}
              </p>
              {capacityPercent !== null && (
                <div className="w-full h-1.5 rounded-full bg-[#3a3122] mt-2 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${capacityPercent}%`, backgroundColor: capacityColor }} />
                </div>
              )}
            </div>
            <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-4">
              <p className="text-xs text-[#8a7f6e]">Class Teacher</p>
              <p className="text-sm font-medium text-[#f3ede2] mt-1.5 truncate">
                {teachers.find(t => t.id === classTeacher)?.name || 'Not assigned'}
              </p>
            </div>
            <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-4">
              <p className="text-xs text-[#8a7f6e]">Subjects</p>
              <p className="text-xl font-bold text-[#f3ede2] mt-1">{assignedSubjects.length}</p>
            </div>
            <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-4">
              <p className="text-xs text-[#8a7f6e]">Sections in {selectedClass}</p>
              <p className="text-xl font-bold text-[#f3ede2] mt-1">{getSelectedClassData()?.sections.length || 0}</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-2 border-b border-[#3a3122] overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-[#e2916d] text-[#f3ede2]'
                    : 'border-transparent text-[#8a7f6e] hover:text-[#b9ad9b]'
                }`}
              >
                {tab.label}
              </button>
            ))}
            {loadingClassDetails && (
              <div className="w-4 h-4 border-2 border-[#e2916d] border-t-transparent rounded-full animate-spin"></div>
            )}
          </div>

          {/* Class Details tab */}
          {activeTab === 'details' && (
            <div className="bg-[#211c15] rounded-xl border border-[#3a3122] overflow-hidden">
              {/* Identity header */}
              <div className="p-6 border-b border-[#3a3122] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-14 h-14 rounded-xl bg-[#332217] flex items-center justify-center flex-shrink-0">
                    <GraduationCap className="w-7 h-7 text-[#e2916d]" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-xl font-semibold text-[#f3ede2]">{selectedClass}</h2>
                      <span className="text-[#4a4030]">&middot;</span>
                      <span className="text-[#b9ad9b] font-medium">Section {selectedSection}</span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          getSelectedClassData()?.isActive
                            ? 'bg-[#173322] text-[#8ff0b8]'
                            : 'bg-[#28221a] text-[#8a7f6e]'
                        }`}
                      >
                        {getSelectedClassData()?.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <p className="text-sm text-[#8a7f6e] mt-1 truncate">
                      {getSelectedClassData()?.description || 'No description added yet'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleEditClass}
                  className="px-4 py-2 bg-[#e2916d] text-white rounded-lg hover:bg-[#f0a67d] transition-colors flex items-center space-x-2 flex-shrink-0"
                >
                  <Edit3 className="w-4 h-4" />
                  <span>Edit Class</span>
                </button>
              </div>

              <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Class Information */}
                <div className="lg:col-span-2">
                  <h4 className="text-sm font-semibold text-[#f3ede2] mb-4 flex items-center">
                    <Users className="w-4 h-4 mr-2 text-[#e2916d]" />
                    Class Information
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">
                    <div>
                      <p className="text-xs text-[#8a7f6e] mb-1">Total Students</p>
                      <p className="text-lg font-semibold text-[#f3ede2]">{classStudents.length}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#8a7f6e] mb-1">Capacity</p>
                      <p className="text-lg font-semibold text-[#f3ede2]">
                        {capacity ? `${classStudents.length} / ${capacity}` : 'Not set'}
                      </p>
                      {capacityPercent !== null && (
                        <div className="w-full h-1.5 rounded-full bg-[#3a3122] mt-2 overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{ width: `${capacityPercent}%`, backgroundColor: capacityColor }}
                          />
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-[#8a7f6e] mb-1">Sections</p>
                      <p className="text-lg font-semibold text-[#f3ede2]">{getSelectedClassData()?.sections.length || 0}</p>
                      <p className="text-xs text-[#8a7f6e] mt-1">{getSelectedClassData()?.sections.join(', ')}</p>
                    </div>
                  </div>

                  {getSelectedClassData()?.description && (
                    <div className="mt-6 pt-5 border-t border-[#3a3122]">
                      <p className="text-xs text-[#8a7f6e] mb-1">Description</p>
                      <p className="text-sm text-[#f3ede2]">{getSelectedClassData()?.description}</p>
                    </div>
                  )}
                </div>

                {/* Class Teacher */}
                <div className="lg:border-l lg:border-[#3a3122] lg:pl-8">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-sm font-semibold text-[#f3ede2] flex items-center">
                      <UserCheck className="w-4 h-4 mr-2 text-[#e2916d]" />
                      Class Teacher
                    </h4>
                    {!isEditingTeacher && (
                      <button
                        onClick={() => setIsEditingTeacher(true)}
                        className="text-[#e2916d] hover:text-[#f0a67d] transition-colors"
                        title="Change class teacher"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>

                  {isEditingTeacher ? (
                    <div className="space-y-3">
                      <select
                        value={classTeacher}
                        onChange={(e) => setClassTeacher(e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
                      >
                        <option value="">Select Teacher</option>
                        {teachers.map((teacher) => (
                          <option key={teacher.id} value={teacher.id}>{teacher.name}</option>
                        ))}
                      </select>
                      <div className="flex space-x-2">
                        <button
                          onClick={handleAssignTeacher}
                          disabled={savingTeacher}
                          className="px-3 py-1.5 text-xs bg-[#e2916d] hover:bg-[#f0a67d] disabled:opacity-50 text-white rounded-lg transition-colors flex items-center space-x-1"
                        >
                          <Save className="w-3 h-3" />
                          <span>{savingTeacher ? 'Saving...' : 'Save'}</span>
                        </button>
                        <button
                          onClick={() => setIsEditingTeacher(false)}
                          className="px-3 py-1.5 text-xs border border-[#3a3122] text-[#b9ad9b] rounded-lg hover:bg-[#28221a] transition-colors flex items-center space-x-1"
                        >
                          <X className="w-3 h-3" />
                          <span>Cancel</span>
                        </button>
                      </div>
                    </div>
                  ) : classTeacher && teachers.find(t => t.id === classTeacher) ? (
                    (() => {
                      const teacher = teachers.find(t => t.id === classTeacher)!;
                      return (
                        <div className="flex items-center gap-3">
                          {teacher.photoURL ? (
                            <img src={teacher.photoURL} alt={teacher.name} className="w-11 h-11 rounded-full object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-11 h-11 rounded-full bg-[#e2916d] flex items-center justify-center flex-shrink-0">
                              <span className="text-white font-medium text-sm">{teacher.name.charAt(0)}</span>
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="text-[#f3ede2] font-medium truncate">{teacher.name}</p>
                            <p className="text-[#8a7f6e] text-xs truncate">{teacher.subject}</p>
                          </div>
                        </div>
                      );
                    })()
                  ) : (
                    <div className="flex items-center gap-3 text-[#8a7f6e]">
                      <div className="w-11 h-11 rounded-full border-2 border-dashed border-[#3a3122] flex items-center justify-center flex-shrink-0">
                        <UserCheck className="w-4 h-4" />
                      </div>
                      <p className="text-sm">No teacher assigned</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Subjects tab */}
          {activeTab === 'subjects' && (
            <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-[#f3ede2] flex items-center">
                  <BookOpen className="w-5 h-5 mr-2 text-[#e2916d]" />
                  Subjects ({assignedUniqueSubjects.length})
                </h3>
                <button
                  onClick={handleOpenEditSubjects}
                  className="px-4 py-2 bg-[#e2916d] text-white rounded-lg hover:bg-[#f0a67d] transition-colors flex items-center space-x-2"
                >
                  <Edit3 className="w-4 h-4" />
                  <span>Edit Subjects</span>
                </button>
              </div>

              {assignedUniqueSubjects.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {assignedUniqueSubjects.map((subject) => (
                    <div key={subject.id} className="flex items-center space-x-3 p-3 border border-[#3a3122] rounded-lg bg-[#28221a]">
                      <BookOpen className="w-4 h-4 text-[#e2916d]" />
                      <div className="flex-1">
                        <span className="text-[#f3ede2] font-medium text-sm">{subject.name}</span>
                        <span className="text-[#8a7f6e] text-xs ml-2">({subject.code})</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <BookOpen className="w-12 h-12 text-[#8a7f6e] mx-auto mb-3" />
                  <p className="text-[#8a7f6e]">No subjects assigned to this class</p>
                  <p className="text-[#b9ad9b] text-sm mt-1">Use "Edit Subjects" to assign subjects</p>
                </div>
              )}
            </div>
          )}

          {/* Students tab */}
          {activeTab === 'students' && (
            <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <h3 className="text-lg font-semibold text-[#f3ede2] flex items-center">
                  <Users className="w-5 h-5 mr-2 text-[#e2916d]" />
                  Students ({filteredClassStudents.length})
                </h3>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a7f6e]" />
                    <input
                      type="text"
                      value={studentSearch}
                      onChange={(e) => setStudentSearch(e.target.value)}
                      placeholder="Search students..."
                      className="pl-9 pr-3 py-2 text-sm border border-[#3a3122] rounded-lg bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
                    />
                  </div>
                  <button
                    onClick={() => exportStudentsToExcel(classStudents)}
                    disabled={classStudents.length === 0}
                    className="flex items-center space-x-2 px-3 py-2 border border-[#3a3122] text-[#b9ad9b] rounded-lg hover:bg-[#28221a] transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="w-4 h-4" />
                    <span>Export</span>
                  </button>
                </div>
              </div>

              {filteredClassStudents.length === 0 ? (
                <p className="text-[#8a7f6e] text-center py-8">
                  {classStudents.length === 0 ? 'No students assigned to this class' : 'No students match your search'}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#3a3122]">
                        <th className="text-left py-2 px-3 text-xs font-medium text-[#8a7f6e] uppercase">Student</th>
                        <th className="text-left py-2 px-3 text-xs font-medium text-[#8a7f6e] uppercase">Gender</th>
                        <th className="text-left py-2 px-3 text-xs font-medium text-[#8a7f6e] uppercase">Age</th>
                        <th className="text-left py-2 px-3 text-xs font-medium text-[#8a7f6e] uppercase">Parent</th>
                        <th className="text-left py-2 px-3 text-xs font-medium text-[#8a7f6e] uppercase">Contact</th>
                        <th className="text-left py-2 px-3 text-xs font-medium text-[#8a7f6e] uppercase">Status</th>
                        <th className="text-left py-2 px-3 text-xs font-medium text-[#8a7f6e] uppercase">Admitted</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#3a3122]">
                      {filteredClassStudents.map(student => (
                        <tr key={student.id} className="hover:bg-[#28221a] transition-colors">
                          <td className="py-3 px-3">
                            <div className="flex items-center space-x-3">
                              {student.photoURL ? (
                                <img src={student.photoURL} alt={student.name} className="w-9 h-9 rounded-full object-cover" />
                              ) : (
                                <div className="w-9 h-9 bg-[#e2916d] rounded-full flex items-center justify-center flex-shrink-0">
                                  <span className="text-white font-medium text-xs">{student.name.charAt(0)}</span>
                                </div>
                              )}
                              <div>
                                <p className="text-[#f3ede2] font-medium">{student.name}</p>
                                <p className="text-[#8a7f6e] text-xs">{student.studentId}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-3 text-[#f3ede2] text-sm">{student.gender}</td>
                          <td className="py-3 px-3 text-[#f3ede2] text-sm">{getAge(student.dateOfBirth)} yrs</td>
                          <td className="py-3 px-3 text-[#f3ede2] text-sm">{student.parentName}</td>
                          <td className="py-3 px-3 text-[#f3ede2] text-sm">
                            <p>{student.parentContact}</p>
                            <p className="text-[#8a7f6e] text-xs">{student.parentEmail}</p>
                          </td>
                          <td className="py-3 px-3">
                            <span className="px-2 py-1 bg-[#173322] text-[#8ff0b8] rounded-full text-xs">{student.admissionStatus}</span>
                          </td>
                          <td className="py-3 px-3 text-[#8a7f6e] text-sm">
                            {student.admissionDate ? formatDate(student.admissionDate) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Attendance tab */}
          {activeTab === 'attendance' && (
            <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <h3 className="text-lg font-semibold text-[#f3ede2] flex items-center">
                  <Calendar className="w-5 h-5 mr-2 text-[#e2916d]" />
                  Attendance
                </h3>
                <input
                  type="date"
                  value={attendanceDate}
                  max={todayIso()}
                  onChange={(e) => setAttendanceDate(e.target.value)}
                  className="px-3 py-2 text-sm border border-[#3a3122] rounded-lg bg-[#17140f] text-[#f3ede2] focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
                />
              </div>

              {classStudents.length === 0 ? (
                <p className="text-[#8a7f6e] text-center py-8">No students in this class to mark attendance for</p>
              ) : loadingAttendance ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-6 h-6 border-2 border-[#e2916d] border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="px-3 py-1 bg-[#173322] text-[#8ff0b8] rounded-full text-xs font-medium">
                      Present: {attendanceSummary.present}
                    </span>
                    <span className="px-3 py-1 bg-[#341a17] text-[#fbccc8] rounded-full text-xs font-medium">
                      Absent: {attendanceSummary.absent}
                    </span>
                    <span className="px-3 py-1 bg-[#332711] text-[#f9daa3] rounded-full text-xs font-medium">
                      Leave: {attendanceSummary.leave}
                    </span>
                    <div className="flex-1" />
                    <button
                      onClick={() => markAll('Present')}
                      className="text-xs px-3 py-1.5 border border-[#3a3122] text-[#b9ad9b] rounded-lg hover:bg-[#28221a] transition-colors"
                    >
                      Mark all present
                    </button>
                  </div>

                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {classStudents.map(student => {
                      const status = attendanceEntries[student.id] || 'Present';
                      return (
                        <div key={student.id} className="flex items-center justify-between p-3 border border-[#3a3122] rounded-lg">
                          <div>
                            <p className="text-[#f3ede2] font-medium text-sm">{student.name}</p>
                            <p className="text-[#8a7f6e] text-xs">{student.studentId}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleAttendanceStatusChange(student.id, 'Present')}
                              className={`p-2 rounded-lg transition-colors ${status === 'Present' ? 'bg-[#173322] text-[#4ade80]' : 'text-[#8a7f6e] hover:bg-[#28221a]'}`}
                              title="Present"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleAttendanceStatusChange(student.id, 'Absent')}
                              className={`p-2 rounded-lg transition-colors ${status === 'Absent' ? 'bg-[#341a17] text-[#f38b83]' : 'text-[#8a7f6e] hover:bg-[#28221a]'}`}
                              title="Absent"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleAttendanceStatusChange(student.id, 'Leave')}
                              className={`p-2 rounded-lg transition-colors ${status === 'Leave' ? 'bg-[#332711] text-[#f0b64c]' : 'text-[#8a7f6e] hover:bg-[#28221a]'}`}
                              title="Leave"
                            >
                              <Clock className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex justify-end mt-4 pt-4 border-t border-[#3a3122]">
                    <button
                      onClick={handleSaveAttendance}
                      disabled={savingAttendance}
                      className="px-4 py-2 bg-[#e2916d] hover:bg-[#f0a67d] disabled:opacity-50 text-white rounded-lg transition-colors flex items-center space-x-2"
                    >
                      <Save className="w-4 h-4" />
                      <span>{savingAttendance ? 'Saving...' : 'Save Attendance'}</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Edit Class Modal */}
      {isEditingClass && (
        <div className="fixed inset-0 bg-always-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[#211c15] rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-[#f3ede2]">Edit Class Details</h2>
              <button onClick={() => setIsEditingClass(false)} className="text-[#8a7f6e] hover:text-[#b9ad9b]">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleUpdateClass(); }} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#f3ede2] mb-2">Class Capacity</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={editClassData.capacity}
                  onChange={(e) => setEditClassData(prev => ({ ...prev, capacity: Number(e.target.value) }))}
                  className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#f3ede2] mb-2">Description</label>
                <textarea
                  value={editClassData.description}
                  onChange={(e) => setEditClassData(prev => ({ ...prev, description: e.target.value }))}
                  className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
                  rows={3}
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-[#3a3122]">
                <button
                  type="button"
                  onClick={() => setIsEditingClass(false)}
                  className="px-4 py-2 text-[#8a7f6e] hover:text-[#b9ad9b] border border-[#3a3122] rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingClass}
                  className="px-4 py-2 bg-[#e2916d] text-white rounded-lg hover:bg-[#f0a67d] disabled:opacity-50 transition-colors"
                >
                  {savingClass ? 'Saving...' : 'Update Class'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Subjects Modal */}
      <Modal isOpen={isEditingSubjects} onClose={() => setIsEditingSubjects(false)} title="Edit Subjects" size="lg">
        <p className="text-sm text-[#b9ad9b] mb-4">
          Select the subjects taught in {selectedClass}{selectedSection ? ` - Section ${selectedSection}` : ''}.
        </p>

        {/* Add a new subject inline */}
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <input
            type="text"
            value={newSubjectName}
            onChange={(e) => setNewSubjectName(e.target.value)}
            placeholder="New subject name (e.g. Robotics)"
            className="flex-1 px-3 py-2 text-sm border border-[#3a3122] rounded-lg bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
          />
          <input
            type="text"
            value={newSubjectCode}
            onChange={(e) => setNewSubjectCode(e.target.value.toUpperCase())}
            placeholder="Code"
            className="sm:w-28 px-3 py-2 text-sm border border-[#3a3122] rounded-lg bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] focus:outline-none focus:ring-2 focus:ring-[#e2916d]"
          />
          <button
            type="button"
            onClick={handleAddSubject}
            disabled={addingSubject || !newSubjectName.trim() || !newSubjectCode.trim()}
            className="px-4 py-2 text-sm bg-[#28221a] hover:bg-[#332711] text-[#f3ede2] border border-[#3a3122] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {addingSubject ? 'Adding...' : '+ Add Subject'}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto border border-[#3a3122] rounded-lg p-3">
          {uniqueSubjects.map((subject) => {
            const groupIds = subjectIdsByKey.get(subjectKey(subject)) || [subject.id];
            const checked = editSubjectIds.some(id => groupIds.includes(id));
            return (
              <label key={subject.id} className="flex items-center space-x-3 p-2 hover:bg-[#28221a] rounded cursor-pointer">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => handleToggleSubject(subject)}
                  className="w-4 h-4 text-[#e2916d] focus:ring-[#e2916d] border-[#4a4030] rounded"
                />
                <div className="flex-1">
                  <span className="text-[#f3ede2] font-medium text-sm">{subject.name}</span>
                  <span className="text-[#8a7f6e] text-xs ml-2">({subject.code})</span>
                </div>
              </label>
            );
          })}
          {uniqueSubjects.length === 0 && (
            <p className="text-[#8a7f6e] text-sm p-2">No subjects yet — add one above to get started.</p>
          )}
        </div>

        <div className="flex justify-end space-x-3 pt-6">
          <button
            onClick={() => setIsEditingSubjects(false)}
            className="px-4 py-2 text-[#8a7f6e] hover:text-[#b9ad9b] border border-[#3a3122] rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveSubjects}
            disabled={savingSubjects}
            className="px-4 py-2 bg-[#e2916d] text-white rounded-lg hover:bg-[#f0a67d] disabled:opacity-50 transition-colors flex items-center space-x-2"
          >
            <Save className="w-4 h-4" />
            <span>{savingSubjects ? 'Saving...' : 'Save Subjects'}</span>
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ClassManagement;
